import React, { useMemo } from 'react';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';
import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Tooltip,
    Legend,
} from 'recharts';

const PIE_COLORS = [
    CHART_COLORS.chart.line1,
    CHART_COLORS.chart.line2,
    CHART_COLORS.chart.line3,
    CHART_COLORS.chart.line4,
    CHART_COLORS.chart.line5,
    CHART_COLORS.chart.line6,
    CHART_COLORS.category.other,
    CHART_COLORS.category.fees,
];

export const JournalExpenseBreakdownChart: React.FC<BaseChartProps> = () => {
    const { finance, journals, specs, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const chartData = useMemo(() => {
        if (!finance?.breakdown) return [];

        const journalIds = new Set(
            (journals || []).map((j: any) => j.id as string)
        );

        const expenseByName: Record<string, number> = {};

        for (const item of finance.breakdown) {
            if (item.type !== 'EXPENSE') continue;

            const entityId = item.entityId as string;
            const channelId = item.channelId as string;
            const isJournalItem =
                entityId?.startsWith('spec-') ||
                entityId?.startsWith('journal-') ||
                journalIds.has(channelId);

            if (isJournalItem) {
                const name = (item.entityName as string) || entityId;
                expenseByName[name] = (expenseByName[name] || 0) + Math.abs(Number(item.value) || 0);
            }
        }

        if (specs) {
            for (const spec of specs) {
                if ((spec as any).type === 'EXPENSE') {
                    const name = (spec as any).name as string;
                    const value = Number((spec as any).value) || 0;
                    if (value > 0 && !expenseByName[name]) {
                        expenseByName[name] = value;
                    }
                }
            }
        }

        return Object.entries(expenseByName)
            .map(([name, value]) => ({ name, value: Math.round(value * 100) / 100 }))
            .sort((a, b) => b.value - a.value);
    }, [finance?.breakdown, journals, specs]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={chartData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius="80%"
                            paddingAngle={2}
                            strokeWidth={0}
                        >
                            {chartData.map((_entry, index) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={PIE_COLORS[index % PIE_COLORS.length]}
                                />
                            ))}
                        </Pie>
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            formatter={(value: number, name: string) => [
                                formatCompactNumber(value, currencySymbol),
                                name,
                            ]}
                        />
                        <Legend
                            formatter={(value: string) =>
                                value.length > 24 ? `${value.slice(0, 24)}...` : value
                            }
                        />
                    </PieChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
